import type { MeResponse } from '@crm/contracts';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { setDisplayTimezone } from '@/lib/datetime';
import { ApiError, api } from './api/client';
import { isAuthRejection } from './auth/session-rules';
import { currentSession, onAuthChange, signOut } from './auth/supabase';
import { ErrorBoundary } from './components/error-boundary';
import { Button } from './components/ui/button';
import { FullPageLoader } from './components/ui/full-page-loader';
import { SkeletonTable } from './components/ui/skeleton';
import { ToastProvider } from './components/ui/toast';
import { AppShell } from './layout/app-shell';
import { queryClient } from './lib/query';
import Login from './routes/Login';

// Route-level splitting: the contacts table and its deps are not in the bundle
// the login page has to download.
const DashboardPage = lazy(() => import('./features/dashboard/dashboard-page'));
const TodayPage = lazy(() => import('./features/today/today-page'));
const WelcomePage = lazy(() => import('./features/welcome/welcome-page'));
const CallsPage = lazy(() => import('./features/calls/calls-page'));
const LeadSheetPage = lazy(() => import('./features/lead-sheet/lead-sheet-page'));
const CoachingPage = lazy(() => import('./features/coaching/coaching-page'));
const CallQueuePage = lazy(() => import('./features/call-queue/call-queue-page'));
const ContactsPage = lazy(() => import('./features/contacts/contacts-page'));
const ContactDetailPage = lazy(() => import('./features/contacts/contact-detail-page'));
const ContactFormPage = lazy(() => import('./features/contacts/contact-form-page'));
const SettingsPage = lazy(() => import('./features/settings/settings-page'));
const CallingSettingsPage = lazy(() => import('./features/settings/calling-settings-page'));
const DataSettingsPage = lazy(() => import('./features/settings/data-settings-page'));
const CalendarSettingsPage = lazy(() => import('./features/settings/calendar-settings-page'));
const CallAnalysisSettingsPage = lazy(() => import('./features/settings/call-analysis-settings-page'));
const WhatsappSettingsPage = lazy(() => import('./features/settings/whatsapp-settings-page'));
const LeadsPage = lazy(() => import('./features/leads/leads-page'));
const LeadFormPage = lazy(() => import('./features/leads/lead-form-page'));
const LeadDetailPage = lazy(() => import('./features/leads/lead-detail-page'));
const FollowUpsPage = lazy(() => import('./features/follow-ups/follow-ups-page'));
const TeamPage = lazy(() => import('./features/team/team-page'));
const TemplatesPage = lazy(() => import('./features/whatsapp/templates-page'));
const TemplateFormPage = lazy(() => import('./features/whatsapp/template-form-page'));
const UsersPage = lazy(() => import('./features/users/users-page'));
const RequirementsPage = lazy(() => import('./features/requirements/requirements-page'));
const SellersPage = lazy(() => import('./features/sellers/sellers-page'));
const DealsPage = lazy(() => import('./features/deals/deals-page'));
const DataSourceListPage = lazy(() => import('./features/data-sources/data-source-list-page'));

type Session = { phase: 'loading' } | { phase: 'out' } | { phase: 'in' };

function Routed({ me, onSignedOut }: { me: MeResponse; onSignedOut: () => void }) {
  /*
   * Every date on screen reads in THIS operator's zone.
   *
   * Set during render rather than in an effect, so the first paint is already
   * right — an effect would show Indian hours to somebody in Dubai and then
   * move them, which is worse than either answer on its own. It is idempotent
   * and returns immediately when the zone has not changed.
   */
  setDisplayTimezone(me.timezone);

  return (
    <Routes>
      <Route element={<AppShell me={me} onSignedOut={onSignedOut} />}>
        {/*
          TODAY IS HOME.
          
          The Dashboard was the first screen an agent saw every morning, and
          it is a reporting screen: counts, charts, a marketing funnel. The
          work is on Today. Opening the app on the numbers and making people
          navigate to the queue is backwards for the five people who use
          this all day, and right only for the one who reads the numbers —
          so the numbers moved to /reports, under More.
        */}
        {/*
          THE SUMMARY OPENS, THE QUEUE IS ONE CLICK AWAY.
          Today is a working queue — it opens into the first thing that is
          wrong, which is right once you are working and wrong at 9am, when
          the question is "what am I walking into". So the summary is the
          index and Today has its own route, which it used to redirect away
          from. Every existing link to /today still lands on the queue.
        */}
        <Route
          index
          element={
            <Suspense fallback={<FullPageLoader />}>
              <WelcomePage />
            </Suspense>
          }
        />
        <Route
          path="today"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <TodayPage />
            </Suspense>
          }
        />
        <Route
          path="reports"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route
          path="contacts"
          element={
            <Suspense fallback={<SkeletonTable rows={8} cols={5} />}>
              <ContactsPage />
            </Suspense>
          }
        />
        {/* Before contacts/:id, or "new" is read as an id. */}
        <Route
          path="contacts/new"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <ContactFormPage />
            </Suspense>
          }
        />
        <Route
          path="contacts/:id"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <ContactDetailPage />
            </Suspense>
          }
        />
        <Route
          path="leads"
          element={
            <Suspense fallback={<SkeletonTable rows={8} cols={5} />}>
              <LeadsPage />
            </Suspense>
          }
        />
        {/* Declared before any /leads/:id route, or "new" reads as an id. */}
        <Route
          path="leads/new"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <LeadFormPage />
            </Suspense>
          }
        />
        <Route
          path="leads/:id"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <LeadDetailPage />
            </Suspense>
          }
        />
        <Route
          path="calls"
          element={
            <Suspense fallback={<SkeletonTable rows={6} cols={4} />}>
              <CallsPage />
            </Suspense>
          }
        />
        <Route
          path="lead-sheet"
          element={
            <Suspense fallback={<SkeletonTable rows={8} cols={3} />}>
              <LeadSheetPage />
            </Suspense>
          }
        />
        <Route
          path="coaching"
          element={
            <Suspense fallback={<SkeletonTable rows={8} cols={3} />}>
              <CoachingPage />
            </Suspense>
          }
        />
        <Route
          path="priority-calls"
          element={
            <Suspense fallback={<SkeletonTable rows={8} cols={3} />}>
              <CallQueuePage />
            </Suspense>
          }
        />
        <Route
          path="follow-ups"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <FollowUpsPage />
            </Suspense>
          }
        />
        <Route
          path="templates"
          element={
            <Suspense fallback={<SkeletonTable rows={6} cols={3} />}>
              <TemplatesPage />
            </Suspense>
          }
        />
        {/* Before templates/:id, or "new" is read as a template id. */}
        <Route
          path="templates/new"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <TemplateFormPage />
            </Suspense>
          }
        />
        <Route
          path="templates/:id"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <TemplateFormPage />
            </Suspense>
          }
        />
        <Route
          path="team"
          element={
            <Suspense fallback={<SkeletonTable rows={6} cols={6} />}>
              <TeamPage />
            </Suspense>
          }
        />
        {/*
          Registered for everyone rather than gated here: the page itself
          checks the role and explains what an operator is missing. A route
          that simply did not exist would bounce them to the dashboard and
          look like a broken link.
        */}
        <Route
          path="users"
          element={
            <Suspense fallback={<SkeletonTable rows={6} cols={5} />}>
              <UsersPage />
            </Suspense>
          }
        />
        <Route
          path="requirements"
          element={
            <Suspense fallback={<SkeletonTable rows={8} cols={5} />}>
              <RequirementsPage />
            </Suspense>
          }
        />
        <Route
          path="sellers"
          element={
            <Suspense fallback={<SkeletonTable rows={8} cols={6} />}>
              <SellersPage />
            </Suspense>
          }
        />
        <Route
          path="deals"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <DealsPage />
            </Suspense>
          }
        />
        {/* One route for every list a tenant configures for itself. */}
        <Route
          path="lists/:key"
          element={
            <Suspense fallback={<SkeletonTable rows={8} cols={5} />}>
              <DataSourceListPage />
            </Suspense>
          }
        />
        {/*
          Settings is a menu, and each subject is its own page. The two have
          nothing to do with each other, and one long page meant scrolling
          past the half you did not come for.
        */}
        <Route
          path="settings"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path="settings/calling"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <CallingSettingsPage />
            </Suspense>
          }
        />
        <Route
          path="settings/data"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <DataSettingsPage />
            </Suspense>
          }
        />
        <Route
          path="settings/calendar"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <CalendarSettingsPage />
            </Suspense>
          }
        />
        <Route
          path="settings/call-analysis"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <CallAnalysisSettingsPage />
            </Suspense>
          }
        />
        <Route
          path="settings/whatsapp"
          element={
            <Suspense fallback={<FullPageLoader />}>
              <WhatsappSettingsPage />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function Authenticated({ onSignedOut }: { onSignedOut: () => void }) {
  const me = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    staleTime: 5 * 60_000,
    /*
     * This used to be `retry: false`, and the effect below signed the
     * operator out on ANY error. One blocked request — wifi dropping for a
     * second, the API restarting, a pooler hiccup — ended the session. It
     * was the single likeliest cause of "it keeps logging me out".
     *
     * A real rejection is still immediate: there is nothing to gain by
     * asking again whether a token the server just refused is good.
     */
    retry: (count, error) => !isAuthRejection(error) && count < 3,
    retryDelay: (count) => Math.min(1000 * 2 ** count, 8000),
  });

  useEffect(() => {
    // A session the API REJECTS is worse than none — drop it rather than
    // showing a shell where every request fails. A session it merely could
    // not confirm is left alone.
    if (me.isError && isAuthRejection(me.error)) {
      void signOut().then(onSignedOut);
    }
  }, [me.isError, me.error, onSignedOut]);

  if (me.isPending) return <FullPageLoader />;

  /*
   * Reachability, not identity. The old code returned null here, so a server
   * that was briefly down showed a blank page under way to a forced sign-out.
   * The session is still good; say so, and offer the retry.
   */
  if (me.isError && !isAuthRejection(me.error)) {
    return (
      <div className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-lg font-bold text-ink">Cannot reach the server</h1>
        <p className="text-sm text-muted">
          You are still signed in. {me.error instanceof ApiError ? me.error.message : 'Something went wrong.'}
        </p>
        <Button onClick={() => void me.refetch()} loading={me.isFetching}>
          Try again
        </Button>
      </div>
    );
  }

  if (!me.data) return null;

  return <Routed me={me.data} onSignedOut={onSignedOut} />;
}

export default function App() {
  const [session, setSession] = useState<Session>({ phase: 'loading' });

  const refresh = useCallback(async () => {
    setSession((await currentSession()) ? { phase: 'in' } : { phase: 'out' });
  }, []);

  const signedOut = useCallback(() => {
    queryClient.clear();
    setSession({ phase: 'out' });
  }, []);

  useEffect(() => {
    void refresh();
    return onAuthChange((s) => {
      if (!s) signedOut();
    });
  }, [refresh, signedOut]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Outside the router so a toast survives the navigation that a create
          usually triggers — the confirmation must outlive the page it came
          from, or "saved" disappears with the form that saved it. */}
      <ToastProvider>
        <ErrorBoundary>
          {session.phase === 'loading' && <FullPageLoader />}
          {session.phase === 'out' && <Login onSignedIn={() => void refresh()} />}
          {session.phase === 'in' && (
            <BrowserRouter>
              <Authenticated onSignedOut={signedOut} />
            </BrowserRouter>
          )}
        </ErrorBoundary>
      </ToastProvider>
    </QueryClientProvider>
  );
}
